/**
 * SavedHarPicker — "From saved HAR" source for Sign off's Batch mode.
 *
 * Lists the backend's saved HARs (GET /api/har), loads the picked one and
 * converts it with utils/harToRows — the same converter the Dashboard's HAR
 * tabs use, so a HAR grades here exactly as it reads there. The parent owns
 * what happens to the rows (LaunchPage swaps them in as the base scope).
 */
import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { listSavedHars, getHar } from '../../api/harClient';
import { harToRows } from '../../utils/harToRows';

const fmt = (t) => (t ? new Date(t).toLocaleString() : '');

export default function SavedHarPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [listing, setListing] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setListing(true);
    setError('');
    const t = setTimeout(() => {
      listSavedHars({ q: q.trim(), limit: 100, sort: 'createdAt' })
        .then((res) => { if (live) setItems(Array.isArray(res) ? res : (res?.items || [])); })
        .catch((e) => { if (live) { setItems([]); setError(e?.message || 'Could not list saved HARs.'); } })
        .finally(() => { if (live) setListing(false); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [open, q]);

  async function pick(it) {
    setLoadingId(it.id);
    setError('');
    try {
      const har = await getHar(it.id);
      const { rows, entryCount, skipped } = harToRows(har);
      if (!rows.length) {
        setError(`"${it.title || it.id}" has no requests that could be read${entryCount ? ` (${skipped} of ${entryCount} entries skipped)` : ''}.`);
        return;
      }
      onPick({ id: it.id, title: it.title || it.pageUrl || it.id, createdAt: it.createdAt || null, rows });
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not load that HAR.');
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Grade a saved HAR</DialogTitle>
      <DialogContent dividers>
        <TextField
          size="small" fullWidth autoFocus
          placeholder="Search saved HARs…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ mb: 1 }}
        />
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {listing && !items.length ? (
          <CircularProgress size={20} />
        ) : !items.length ? (
          <Typography variant="body2" color="text.secondary">No saved HARs{q.trim() ? ` match “${q.trim()}”` : ''}.</Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 420, overflow: 'auto' }}>
            {items.map((it) => (
              <ListItemButton key={it.id} disabled={!!loadingId} onClick={() => pick(it)}>
                <ListItemText
                  primary={it.title || it.pageUrl || it.id}
                  secondary={[fmt(it.createdAt), it.favorite ? '★ favorite' : ''].filter(Boolean).join(' · ')}
                />
                {loadingId === it.id && <CircularProgress size={16} />}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
